/**
 * Core HUD overlay (PXL). The always-on surface during play: vitals (HP/stamina/
 * XP/level + dodge), currency (Shard + Gleam), ability bar, buff/debuff tracker,
 * minimap, quest tracker, target/boss frame, interaction prompt, chat dock and
 * toast feed. Connection/tick/health/dodge come from Game's `status`; everything
 * else from the client store (private pushes — never the per-tick path, AD-031).
 */

import { useEffect, useRef, useState } from 'react';

import type { GameStatus } from '../game/Game.js';
import { isGameKey, isImeComposing } from '../game/keyTarget.js';
import { requestBasicAttack, requestDodge } from '../game/input.js';
import { AbilityPressBuffer } from './abilityBuffer.js';
import { abilitySlotForKey, sendAbilityPress } from './abilityHotkeys.js';
import { interactionPrompt } from './interactionPrompt.js';
import { useGame } from './net-context.js';
import { requestOpenPanel } from './Shell.js';
import { AbilityIcon, BASIC_ATTACK_ICON } from './AbilityIcon.js';
import { HudIcon } from './HudIcon.js';
import { BugReportHudButton } from './BugReport.js';
import { IconGlyph } from './widgets.js';
import { QuestCompleteCard } from './QuestCompleteCard.js';
import { worldFrame } from './worldFrame.js';
import {
  humanizeId,
  nameOf,
  npcRolesText,
  objectiveText,
  stackName,
  stationLabel,
} from './playerText.js';
import {
  WAYPOINT_ARRIVED_M,
  minimapProjector,
  screenAngleDeg,
  stationsIn,
  trackedObjective,
  waypointDistanceM,
  waypointFor,
  type Tracked,
  type Waypoint,
} from './wayfinding.js';
import { npcMarkerKind, offerNpcSet, readyTurnInNpcs } from '@great-cloud/shared/client';
import { NPC_MARKER_GLYPHS, markerPrefix } from '../game/npcs/marker.js';
import { findHint } from './questSignals.js';
import { useGuestMode } from '../guest/guestContext.js';
import { GuestDeathCard, GuestDemoBanner } from '../guest/GuestScreens.js';
import { guestCanGather } from '../guest/guestWalls.js';
import { isCompactLayout } from './layout/layoutMode.js';
import { useLayoutMode, useMobileControlsEnabled } from './layout/useLayout.js';
import { TouchControlsOverlay } from './touch/TouchControlsOverlay.js';
import { AbilitySlotFill, slotStateClass, useAbilitySlotViews } from './AbilitySlotFill.js';
import { HintCard, inputMode } from './hints/HintCard.js';
import { mobileControlsChosen, setLayoutPrefs } from './layout/layoutPrefs.js';
import { HowToPlayCard } from './onboarding/HowToPlayCard.js';
import {
  howToPlayPlan,
  markHowToPlaySeen,
  pageStorage,
  readHowToPlaySeen,
  type HowToPlayPlan,
} from './onboarding/howToPlay.js';
import { targetFrameEntity } from './targetFrame.js';
import { COMPACT_TOAST_ROWS, toastLabel } from '../state/toasts.js';

export function Hud({ status, onLeave }: { status: GameStatus; onLeave: () => void }): JSX.Element {
  const { state, conn, store } = useGame();
  const [chatInput, setChatInput] = useState('');
  // AD-794 §6.2: in the guest demo the walled affordances are HIDDEN, not left to fail — no chat
  // dock (every channel is walled), no Gleam pill (no store) — and a thin banner says so.
  const guest = useGuestMode();
  // Portrait / short-landscape layouts (mobile-layout.md §3): playfield first — the minimap,
  // quest tracker and chat collapse behind two dock toggles until the player opens them.
  const layout = useLayoutMode();
  const compact = isCompactLayout(layout);
  const [mapOpen, setMapOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  // AD-833 (guest-demo.md §5.6.3): the death card's "Try again" was pressed; it holds the button
  // until the body rises (the server waits out the ordinary delay), then resets with `dead`.
  const [retrying, setRetrying] = useState(false);
  useEffect(() => {
    if (!status.dead) setRetrying(false);
  }, [status.dead]);
  // q-demo-touch-first-run-onboarding (guest-demo.md §5.7.4): the demo's first entry opens with
  // one "How to play" card (and, on touch, the on-screen-controls offer); once per browser.
  const [howTo, setHowTo] = useState<HowToPlayPlan | null>(() =>
    guest
      ? howToPlayPlan({
          input: inputMode(),
          seen: readHowToPlaySeen(pageStorage()),
          controlsChosen: mobileControlsChosen(pageStorage()),
        })
      : null,
  );
  const [controlsAnswer, setControlsAnswer] = useState<boolean | null>(null);
  const showMap = !compact || mapOpen;
  const showChat = !compact || chatOpen;
  // T3 (mobile-layout.md §7): the player switched the on-screen controls ON — the stick and
  // the touch bar (slots 1–6 + Dodge) replace the ability row. Off (the default): as before.
  const touchControls = useMobileControlsEnabled();
  // One leave button: the currency row's on desktop, the dock column's (a 44 px target, apart
  // from the strip) in the compact layouts. A guest has NONE here: End demo moved off the play
  // area into the Menu behind a confirm (guest-demo.md §5.6.4; ui/Shell.tsx).
  const leaveButton = guest ? null : (
    <button
      type="button"
      className={compact ? 'hud-dock-btn hud-leave interactive' : 'pxl-btn tiny interactive'}
      onClick={onLeave}
      aria-label="Leave the world"
      title="Leave the world"
      data-testid="hud-leave"
    >
      {/* q-hud-panel-button-icons: an icon, never a bare word; the caption only in the dock. */}
      <HudIcon id="leave" size={compact ? 24 : 16} />
      {compact ? <span className="hud-caption">Leave</span> : null}
    </button>
  );

  const self = state.selfEntityId != null ? state.entities.get(state.selfEntityId) : undefined;
  // The target frame shows the SOFT target — the enemy an ability press will hit
  // (combat.md §Control model) — and nothing when there is none (targetFrame.ts).
  const target = targetFrameEntity(store.softTarget(), state.entities);
  const stamina = state.stamina;
  const lvl = state.level;

  // Contextual interaction prompt: nearest NON-depleted gather node within range
  // (reads live node state, so a depleted node hides the prompt until it respawns),
  // or the nearest NPC within talk range — whichever is closer owns the E key.
  // None inside a dungeon run: the body is in the instance frame, not the world's.
  const selfAt = self?.x != null && self.z != null ? { x: self.x, z: self.z } : null;
  const prompt = interactionPrompt({
    self: selfAt,
    inDungeonRun: status.inDungeonRun,
    nodes: state.nodes,
    npcs: state.ack?.npcs ?? [],
    // q-demo-crafting-clarity: a placed bench in reach offers "Craft at <station>".
    stations: stationsIn(state.ack?.zoneId),
    // A guest is never offered a node its kit's ranks cannot open (AD-794 §6.2).
    ...(guest
      ? {
          canGather: (resource: string) =>
            guestCanGather(resource, state.resourceTiers, state.resourceSkills, state.masteries),
        }
      : {}),
  });
  // q-player-names-and-wayfinding: the step the player is on and where its target stands — the
  // wayfinder dial points at it and the minimap pins it. None inside a dungeon run (other frame).
  const tracked = status.inDungeonRun ? null : trackedObjective(state.quests?.quests ?? []);
  const frame = worldFrame(state);
  const waypoint = tracked
    ? waypointFor(tracked, {
        self: selfAt,
        npcs: frame.npcs,
        nodes: frame.nodes,
        entities: state.entities.values(),
        stations: stationsIn(state.ack?.zoneId),
        transitions: state.inDungeonRun ? [] : (state.ack?.world.transitions ?? []),
        enemyAreas: state.ack?.enemyAreas ?? [],
      })
    : null;
  // AD-867: the NPCs a quest is ready to hand in at — a "!" on the minimap (the world marks
  // them too, Game.onQuestLog). Generic over quests: read from status, never a quest id.
  const turnInAt = readyTurnInNpcs(state.quests?.quests);
  // q-quest-marker-glyph-semantics: the NPCs with something for the player (the "?").
  const offerAt = offerNpcSet(state.quests);
  const talkTo = prompt?.kind === 'npc' ? prompt.npc : undefined;
  const node = prompt?.kind === 'node' ? prompt.node : undefined;
  const bench = prompt?.kind === 'station' ? prompt.station : undefined;

  // E gathers the prompted node (sprint-assembly-1, AD-647) or talks to the prompted NPC.
  // Same send as the button, so the server stays the only judge of range and charges;
  // typing-guarded like the Shell's panel hotkeys (E is not a panel key).
  const promptKey = talkTo
    ? `npc:${talkTo.id}`
    : node
      ? `node:${node.id}`
      : bench
        ? `station:${bench.station}` // id-ok: effect key
        : undefined;
  useEffect(() => {
    if (promptKey === undefined) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'e' && e.key !== 'E') return;
      if (!isGameKey(e, document.activeElement) || e.repeat) return;
      if (talkTo) startTalk();
      else if (bench) startCraft();
      else startGather();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [promptKey, conn]);

  // Every ability press names the soft target, picked AT the press (combat-depth.md §3.2);
  // without a target the server's damage effect does nothing while stamina is spent.
  const abilities = abilityList(state.loadout);
  // AD-849: each slot's cooldown fill and stamina dim, from the server's numbers (display only).
  const slotViews = useAbilitySlotViews(
    abilities.map((a) => a.id),
    state.abilityCooldowns,
    state.loadout?.abilities,
    stamina?.current,
  );
  // Keys, the bar and the touch overlay share this one press (`sendAbilityPress`), through the
  // input buffer (q-combat-game-feel): a press just before ready is held and sent at ready, with
  // the soft target picked when it is SENT. Refs keep the key handler off stale cooldowns.
  const cooldownsRef = useRef(state.abilityCooldowns);
  cooldownsRef.current = state.abilityCooldowns;
  const linkRef = useRef({ conn, store });
  linkRef.current = { conn, store };
  const bufferRef = useRef<AbilityPressBuffer | null>(null);
  if (bufferRef.current === null) {
    bufferRef.current = new AbilityPressBuffer({
      send: (abilityId) =>
        sendAbilityPress(
          (t, d) => linkRef.current.conn.send(t, d),
          () => linkRef.current.store.softTarget(),
          abilityId,
        ),
      schedule: (fn, ms) => {
        const timer = setTimeout(fn, ms);
        return () => clearTimeout(timer);
      },
      now: () => Date.now(),
    });
  }
  useEffect(() => {
    const buffer = bufferRef.current;
    const off = store.onAbilityResult((d) =>
      buffer?.answered(d.abilityId, d.ok, d.reason, d.readyInMs),
    );
    return () => {
      off();
      buffer?.cancel();
    };
  }, [store]);
  function pressAbility(abilityId: string): void {
    bufferRef.current?.press(abilityId, cooldownsRef.current);
  }

  // Keys 1–6 press ability slots 1–6 (typing-guarded like E and the panel hotkeys).
  const abilityKey = abilities.map((a) => a.id).join(',');
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const slot = abilitySlotForKey(e.key);
      if (slot === null || e.repeat || !isGameKey(e, document.activeElement)) return;
      const ability = abilities[slot];
      if (ability === undefined) return;
      e.preventDefault();
      pressAbility(ability.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [abilityKey, conn, store]);

  // Talk opens the Quests panel, where the NPC's dialogue (offers, turn-ins) renders.
  function startTalk(): void {
    if (talkTo === undefined) return;
    conn.send('talkIntent', { npcId: talkTo.id });
    requestOpenPanel('quests');
  }

  // q-demo-crafting-clarity: at a bench the prompt IS the way into crafting — no menu to find.
  function startCraft(): void {
    requestOpenPanel('crafting');
  }

  // AD-665: the press starts the CHANNEL presentation at once (bar, clip, tool) and sends
  // the intent; the server's `gatherResult` ends it (yield, refusal or `interrupted`).
  function startGather(): void {
    if (node === undefined) return;
    if (store.beginGatherChannel(node.id, node.resource, performance.now())) {
      conn.send('interactIntent', { nodeId: node.id });
    }
  }

  return (
    <div className={touchControls ? 'pxl-hud hud-touch-on' : 'pxl-hud'} data-layout={layout}>
      {/* T3 on-screen controls (mobile-layout.md §7): FIRST, so every other HUD element paints
          above the stick zone and keeps its own taps. */}
      {touchControls ? (
        <TouchControlsOverlay
          abilities={abilities}
          onAbility={pressAbility}
          dodgeReadyPct={status.dodgeReadyPct}
          slotViews={slotViews}
        />
      ) : null}
      {/* compact layouts: the collapsed surfaces' toggles (≥ 44 px targets) */}
      {compact ? (
        <div className="hud-dock-toggles">
          <button
            type="button"
            className={`hud-dock-btn interactive${mapOpen ? ' open' : ''}`}
            aria-pressed={mapOpen}
            onClick={() => setMapOpen((v) => !v)}
            aria-label="Map"
            data-testid="hud-toggle-map"
          >
            <HudIcon id="map" />
            <span className="hud-caption">Map</span>
          </button>
          {guest ? null : (
            <button
              type="button"
              className={`hud-dock-btn interactive${chatOpen ? ' open' : ''}`}
              aria-pressed={chatOpen}
              onClick={() => setChatOpen((v) => !v)}
              aria-label="Chat"
              data-testid="hud-toggle-chat"
            >
              <HudIcon id="chat" />
              <span className="hud-caption">Chat</span>
            </button>
          )}
          {leaveButton}
        </div>
      ) : null}

      {/* minimap */}
      {!showMap || localStorage.getItem('pxl.minimap') === '1' ? null : (
        <div className="hud-minimap">
          <Minimap
            state={state}
            waypoint={waypoint}
            self={selfAt}
            cameraYaw={status.cameraYaw ?? 0}
            turnInAt={turnInAt}
            targetResource={
              tracked?.kind === 'objective' && tracked.objective.verb === 'gather'
                ? (tracked.objective.target ?? null)
                : null
            }
          />
        </div>
      )}

      {/* wayfinder — an arrow to the tracked objective's target, with the step in words */}
      {status.inDungeonRun || tracked === null ? null : (
        <Wayfinder
          cameraYaw={status.cameraYaw ?? 0}
          self={selfAt}
          tracked={tracked}
          waypoint={waypoint}
        />
      )}

      {/* connection ministats (top-left, under minimap) */}
      <div className="hud-netstats pxl-faint">
        {status.phase} · {Math.round(status.rttMs)}ms · tick {status.serverTick} · {status.players}p
        {/* D1: named only off the default, so the 15 Hz HUD is unchanged (owner 10-vs-15 playtest). */}
        {status.snapshotHz < 15 ? ` · ${status.snapshotHz} Hz` : null}
      </div>

      {/* currency (in guest mode the demo banner leads the row; top centre is the target frame's) */}
      <div className="hud-currency">
        {guest ? (
          <GuestDemoBanner
            registrationOpen={guest.registrationOpen}
            ctaTargets={guest.ctaTargets}
          />
        ) : null}
        <div className="hud-pill" title="Shard — soft currency">
          <IconGlyph code="shard" label="◈" assetRef="TGC-UI-011" size={18} />
          <span style={{ color: 'var(--pxl-shard)' }}>{state.wallet?.shardBalance ?? '—'}</span>
        </div>
        {guest ? null : (
          <div className="hud-pill" title="Gleam — premium, cosmetic-only">
            <IconGlyph code="gleam" label="✦" assetRef="TGC-UI-011" size={18} />
            <span style={{ color: 'var(--pxl-gleam)' }}>
              {gleamBalance(state.gleamWallet) ?? '—'}
            </span>
          </div>
        )}
        {/* q-bug-report-button: in a dungeon run, one click reports a bug with the live run. */}
        {status.inDungeonRun ? <BugReportHudButton /> : null}
        {compact ? null : leaveButton}
      </div>

      {/* toasts — they age out on their own (state/toasts.ts). On a phone (compact) only the
          newest two show and none takes a tap: a tap on the world must reach the world
          (q-hud-toasts-never-expire, mobile-layout.md §3.1). Desktop keeps click-to-dismiss. */}
      <div className="hud-toasts" data-testid="hud-toasts" data-hud-notice>
        {(compact ? state.toasts.slice(-COMPACT_TOAST_ROWS) : state.toasts).map((t) => (
          <div
            key={t.id}
            className={`hud-toast ${t.kind}`}
            data-toast-id={t.id}
            onClick={compact ? undefined : () => store.dismissToast(t.id)}
          >
            {toastLabel(t)}
          </div>
        ))}
      </div>

      {/* A confirmed turn-in: the quest, the NPC's line and the rewards (AD-870 Am.1). */}
      <QuestCompleteCard />

      {/* loot feed — explicit drops from your own kills (UIE-307). Desktop only: on a phone
          every entry's own "Loot: …" toast already says it, and a second column covered the
          play area (mobile-layout.md §3.1). */}
      {!compact && state.loot.length > 0 ? (
        <div className="hud-loot">
          {state.loot
            .slice(-4)
            .reverse()
            .map((l) => (
              <div key={l.id} className="hud-loot-entry">
                <IconGlyph code="loot" label="◆" assetRef="TGC-UI-013" size={16} />
                <div className="grow">
                  {l.items.map((g, i) => (
                    <span key={`i${i}`} className={`r-${g.rarity}`}>
                      {stackName(g.contentId)}
                      {g.count > 1 ? ` ×${g.count}` : ''}
                      {i < l.items.length - 1 || l.resources.length > 0 ? ' · ' : ''}
                    </span>
                  ))}
                  {l.resources.map((r, i) => (
                    <span key={`r${i}`} className="pxl-muted">
                      {stackName(r.contentId)} ×{r.quantity}
                      {i < l.resources.length - 1 ? ' · ' : ''}
                    </span>
                  ))}
                </div>
              </div>
            ))}
        </div>
      ) : null}

      {/* target / boss frame */}
      {target ? (
        <div className="hud-target">
          <div className="pxl-stat">
            <span className="k">{target.name ?? `enemy ${target.id}`}</span>
            <span className="v">
              {Math.round(target.health ?? 0)}/{target.healthMax ?? '?'}
            </span>
          </div>
          <div className="pxl-bar" style={{ height: 9 }}>
            <div
              className="fill"
              style={{
                width: `${pct(target.health, target.healthMax)}%`,
                background: 'var(--pxl-hp)',
              }}
            />
          </div>
        </div>
      ) : null}

      {/* quest tracker */}
      {/* AD-867: a quest stays on the tracker once met — ready ones first, "Return to <NPC>";
          a met objective is ticked, never dropped, so the player sees it is done. */}
      {showMap && state.quests && state.quests.quests.some((q) => q.status !== 'turned_in') ? (
        <div className="hud-quests" data-testid="hud-quests">
          <div className="pxl-section-title">Quests</div>
          {state.quests.quests
            .filter((q) => q.status !== 'turned_in')
            .sort((x, y) => Number(y.status === 'complete') - Number(x.status === 'complete'))
            .slice(0, 3)
            .map((q) =>
              q.status === 'complete' ? (
                <div key={q.questId} style={{ marginBottom: 4 }} data-testid="hud-quest-ready">
                  <div style={{ fontSize: 12 }}>
                    <span className="hud-quest-ready">{NPC_MARKER_GLYPHS.turnIn}</span> {q.name}
                  </div>
                  <div className="hud-quest-done">
                    Ready — return to {nameOf('npcs', q.turnInNpc)}
                  </div>
                </div>
              ) : (
                <div key={q.questId} style={{ marginBottom: 4 }}>
                  <div style={{ fontSize: 12 }}>{q.name}</div>
                  {q.objectives.slice(0, 3).map((o, i) => (
                    <div key={i} className={o.done ? 'hud-quest-done' : 'pxl-faint'}>
                      {o.done ? '✓ ' : ''}
                      {objectiveText(o)} {o.progress}/{o.count}
                      {/* Desk #279: what to look for, and where (AD-867 area) */}
                      {tracked?.kind === 'objective' &&
                      tracked.objective === o &&
                      waypoint?.kind === 'area' ? (
                        <div className="hud-quest-find" data-testid="hud-quest-find">
                          {findHint(waypoint.label)}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ),
            )}
        </div>
      ) : null}

      {/* buffs */}
      {state.buffs.length > 0 ? (
        <div className="hud-buffs">
          {state.buffs.map((b, i) => (
            <div
              key={i}
              className={`hud-buff${b.isDebuff ? ' debuff' : ''}`}
              title={`${humanizeId(b.abilityId)}: ${b.magnitude > 0 ? '+' : ''}${b.magnitude} ${b.stat}`}
            >
              {b.stat.slice(0, 2)}
            </div>
          ))}
        </div>
      ) : null}

      {/* interaction prompt — or, while gathering, the channel's progress (AD-665) */}
      {state.gatherChannel ? (
        <div className="hud-prompt">
          <GatherChannelBar channel={state.gatherChannel} />
        </div>
      ) : talkTo ? (
        <div className="hud-prompt interactive">
          <button className="pxl-btn primary" onClick={startTalk} data-testid="talk-prompt">
            {compact ? '' : '[E] '}
            {markerPrefix(npcMarkerKind(talkTo.id, talkTo.roles, turnInAt, offerAt))}Talk to{' '}
            {talkTo.name}
          </button>
        </div>
      ) : node ? (
        <div className="hud-prompt interactive">
          <button className="pxl-btn primary" onClick={startGather}>
            {compact ? '' : '[E] '}Gather {nameOf('resources', node.resource)}
          </button>
        </div>
      ) : bench ? (
        <div className="hud-prompt interactive">
          <button className="pxl-btn primary" onClick={startCraft} data-testid="craft-prompt">
            {compact ? '' : '[E] '}Craft at {stationLabel(bench.station)}
          </button>
        </div>
      ) : null}

      {/* AD-844 (guest-demo.md §5.7): the demo's just-in-time hints — one at a time, shown once,
          bottom centre above the prompt; never under the toasts (AD-839). Guest mode only. */}
      {guest && !status.dead && howTo === null ? <HintCard status={status} /> : null}

      {/* q-demo-touch-first-run-onboarding: the first-run "How to play" step; the hints wait for it. */}
      {guest && !status.dead && howTo !== null ? (
        <HowToPlayCard
          plan={howTo}
          controlsAnswer={controlsAnswer}
          onControls={(on) => {
            setControlsAnswer(on);
            setLayoutPrefs({ mobileControls: on });
          }}
          onGo={() => {
            markHowToPlaySeen(pageStorage());
            setHowTo(null);
          }}
        />
      ) : null}

      {/* chat dock — absent in the guest demo (chat is walled on every channel, AD-794 §3.2) */}
      {guest || !showChat ? null : (
        <div className="hud-chatdock">
          <div className="hud-chatlog">
            {state.chat.slice(-8).map((m, i) => (
              <div key={i}>
                <span className={`chan-${m.channel}`}>[{m.channel}]</span> {m.fromName}: {m.body}
              </div>
            ))}
          </div>
          <div className="hud-chat-input">
            <input
              placeholder="press Enter to chat (zone)"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                // An IME's Enter commits the candidate; it must not send the half-typed line.
                if (isImeComposing(e.nativeEvent)) return;
                if (e.key === 'Escape') e.currentTarget.blur();
                if (e.key === 'Enter' && chatInput.trim()) {
                  conn.send('chatSend', { channel: 'zone', body: chatInput.trim() });
                  setChatInput('');
                  // Hand the keys back to the world: WASD moves again without a click.
                  e.currentTarget.blur();
                }
              }}
            />
          </div>
        </div>
      )}

      {/* ability bar — replaced by the touch bar when the on-screen controls are on (T3) */}
      {touchControls ? null : (
        <div className="hud-abilities">
          {abilities.map((a, i) => (
            <div
              key={a.id}
              className={`hud-slot interactive${slotStateClass(slotViews[a.id])}`}
              role="button"
              aria-label={`ability ${i + 1}: ${humanizeId(a.id)}`}
              title={`${humanizeId(a.id)} [${i + 1}]`}
              data-testid={`ability-slot-${i + 1}`}
              data-cd={slotViews[a.id]?.state ?? 'ready'}
              onClick={() => pressAbility(a.id)}
            >
              <span className="key">{i + 1}</span>
              <AbilityIcon abilityId={a.id} label={humanizeId(a.id)} size={28} />
              <AbilitySlotFill view={slotViews[a.id]} />
            </div>
          ))}
          {compact ? (
            // AD-828 (owner, 2026-10-23): the compact (phone) bar's seventh button is DODGE — the
            // basic attack is a tap on a hostile there (T1, AD-817), as T3's overlay already does
            // (mobile-layout.md §7.2). Fires on pointerdown, the same queued dodge as Space
            // (`requestDodge`); the fill is the dodge readiness the vitals' bar draws.
            <div
              role="button"
              aria-label="dodge"
              className={`hud-slot hud-dodge interactive${status.dodgeReadyPct >= 1 ? ' ready' : ''}`}
              title="dodge (Space)"
              data-testid="hud-dodge"
              onPointerDown={(e) => {
                e.preventDefault();
                requestDodge();
              }}
            >
              <span
                className="hud-dodge-fill"
                style={{
                  height: `${Math.round(Math.max(0, Math.min(1, status.dodgeReadyPct)) * 100)}%`,
                }}
              />
              <HudIcon id="dodge" size={24} />
              <span className="hud-dodge-label hud-caption">Dodge</span>
            </div>
          ) : (
            // The same path as F: soft target, then face / walk-to / swing (combat.md §Control model).
            <div
              role="button"
              aria-label="basic attack"
              className="hud-slot interactive"
              title="basic attack (F)"
              data-testid="basic-attack"
              onClick={requestBasicAttack}
            >
              <span className="key">F</span>
              <AbilityIcon abilityId={BASIC_ATTACK_ICON} label="basic attack" size={28} />
            </div>
          )}
        </div>
      )}

      {/* vitals */}
      <div className="hud-vitals">
        <div className="pxl-stat" style={{ padding: 0 }}>
          <span className="k">{state.ack?.you.name ?? 'You'}</span>
          <span className="v">Lv {lvl?.level ?? 1}</span>
        </div>
        <div className="pxl-bar">
          <div
            className="fill"
            style={{
              width: `${pct(status.health, status.healthMax)}%`,
              background: 'var(--pxl-hp)',
            }}
          />
          <div className="label">
            {Math.max(0, Math.round(status.health))} / {status.healthMax}
          </div>
        </div>
        {stamina ? (
          <div className="pxl-bar" style={{ height: 9 }}>
            <div
              className="fill"
              style={{
                width: `${pct(stamina.current, stamina.max)}%`,
                background: 'var(--pxl-stam)',
              }}
            />
          </div>
        ) : null}
        {lvl ? (
          <div className="pxl-bar" style={{ height: 7 }}>
            <div
              className="fill"
              style={{
                width: `${pct(lvl.xp - lvl.xpIntoLevel, (lvl.xpForNextLevel ?? lvl.xp) - lvl.xpIntoLevel)}%`,
                background: 'var(--pxl-xp)',
              }}
            />
          </div>
        ) : null}
        <div className="pxl-bar" style={{ height: 6 }} title="dodge cooldown">
          <div className="fill" style={{ width: `${Math.round(status.dodgeReadyPct * 100)}%` }} />
        </div>
      </div>

      {status.flash ? (
        <div className={`flash ${status.flash.kind}`}>{status.flash.text}</div>
      ) : null}
      {status.dead && guest ? (
        <GuestDeathCard
          registrationOpen={guest.registrationOpen}
          ctaTargets={guest.ctaTargets}
          retrying={retrying}
          onTryAgain={() => {
            setRetrying(true);
            conn.send('respawnIntent', {});
          }}
        />
      ) : status.dead ? (
        <div className="flash hit">DOWN — respawning…</div>
      ) : null}
    </div>
  );
}

// ---- helpers ----

function pct(v: number | undefined, max: number | undefined): number {
  if (!v || !max || max <= 0) return 0;
  return Math.max(0, Math.min(100, (v / max) * 100));
}

function abilityList(
  loadout: { weaponAbilities: string[]; chosenAbilities: string[] } | null,
): { id: string; kind: string }[] {
  if (!loadout) return [];
  return [
    ...loadout.weaponAbilities.map((id) => ({ id, kind: 'weapon' })),
    ...loadout.chosenAbilities.map((id) => ({ id, kind: 'chosen' })),
  ];
}

function gleamBalance(w: unknown): number | undefined {
  return w && typeof w === 'object' && 'balance' in w
    ? (w as { balance: number }).balance
    : undefined;
}

/**
 * The wayfinder (q-player-names-and-wayfinding; docs/design/ui-hud-wayfinding.md): a gold arrow
 * on a small dial pointing, on screen, at the tracked objective's target, beside the objective in
 * words and the distance. Shown while a quest is in play; never takes a tap. No cardinal letters
 * (wayfinding.ts `screenOffset` says why).
 */
function Wayfinder({
  cameraYaw,
  self,
  tracked,
  waypoint,
}: {
  cameraYaw: number;
  self: { x: number; z: number } | null;
  tracked: Tracked | null;
  waypoint: Waypoint | null;
}): JSX.Element {
  const dist = waypoint ? waypointDistanceM(self, waypoint) : null;
  const area = waypoint?.kind === 'area';
  // An area is reached once the player is inside it (distance to its edge 0); a pin within 3 m.
  const arrived = dist !== null && (area ? dist === 0 : dist <= WAYPOINT_ARRIVED_M);
  const arrow =
    waypoint && self && !arrived
      ? screenAngleDeg(waypoint.x - self.x, waypoint.z - self.z, cameraYaw)
      : null;
  const step = tracked
    ? tracked.kind === 'turnIn'
      ? `Return to ${nameOf('npcs', tracked.npcId)}`
      : objectiveText(tracked.objective)
    : null;
  const where = waypoint
    ? area
      ? `${waypoint.label} · ${arrived ? 'search around here' : dist !== null ? `area ${dist} m` : 'area'}`
      : `${waypoint.label}${arrived ? ' · here' : dist !== null ? ` · ${dist} m` : ''}`
    : step
      ? 'not in sight yet'
      : null;
  // data-x/z (the target's world point) and data-angle: the visual lane's waitWayfinder step
  // checks the arrow against where the target draws on screen (touch-play.mjs).
  return (
    <div
      className="hud-wayfinder"
      data-testid="hud-wayfinder"
      data-x={waypoint ? waypoint.x.toFixed(2) : undefined}
      data-z={waypoint ? waypoint.z.toFixed(2) : undefined}
    >
      <div className="wf-dial" aria-hidden="true">
        {arrow !== null ? (
          // A notched navigation arrowhead: its point reads at a glance on a phone (lane run
          // 20261024T224919Z: a plain triangle read as any of three directions).
          <svg
            className="wf-arrow"
            data-testid="wf-arrow"
            data-angle={arrow.toFixed(1)}
            viewBox="-16 -16 32 32"
            width={34}
            height={34}
            style={{ transform: `translate(-50%, -50%) rotate(${arrow}deg)` }}
          >
            <polygon points="0,-14 10,11 0,5 -10,11" />
          </svg>
        ) : (
          <span className="wf-dot" />
        )}
      </div>
      {step ? (
        <div className="wf-text">
          <div className="wf-step" data-testid="wf-step">
            {step}
          </div>
          {where ? (
            <div className="pxl-faint" data-testid="wf-where">
              {where}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** AD-867: resource nodes and creatures show on the minimap only this near the player. */
const MINIMAP_REVEAL_M = 30;

function Minimap({
  state,
  waypoint,
  self,
  cameraYaw,
  turnInAt,
  targetResource,
}: {
  state: ReturnType<typeof useGame>['state'];
  waypoint: Waypoint | null;
  self: { x: number; z: number } | null;
  cameraYaw: number;
  turnInAt: ReadonlySet<string>;
  /** The tracked gather objective's resource (Desk #279), or null. */
  targetResource: string | null;
}): JSX.Element {
  // In a run: the run's entities only — the world's nodes/NPCs are another frame.
  const frame = worldFrame(state);
  const findResource = targetResource;
  // AD-867: fixed places (NPCs) are always on the map; what the player must FIND — resource nodes
  // and creatures — shows only near them (what they can see), so an objective's area circle
  // guides without giving the spot away.
  const near = (x?: number, z?: number): boolean =>
    x != null &&
    z != null &&
    (self === null || Math.hypot(x - self.x, z - self.z) <= MINIMAP_REVEAL_M);
  const nodes = frame.nodes.filter((n) => near(n.x, n.z));
  const npcs = frame.npcs;
  const entities = [...state.entities.values()].filter(
    (e) => e.id === state.selfEntityId || e.kind !== 'enemy' || near(e.x, e.z),
  );
  const pts: { x: number; z: number }[] = [];
  for (const n of nodes) pts.push({ x: n.x, z: n.z });
  for (const npc of npcs) pts.push({ x: npc.x, z: npc.z });
  for (const e of entities) if (e.x != null && e.z != null) pts.push({ x: e.x, z: e.z });
  if (waypoint) {
    const r = waypoint.radiusM ?? 0;
    pts.push({ x: waypoint.x - r, z: waypoint.z - r }, { x: waypoint.x + r, z: waypoint.z + r });
  }
  if (pts.length === 0)
    return (
      <div className="pxl-faint" style={{ padding: 8 }}>
        map
      </div>
    );
  // Heading-up around the player, one scale for both axes (wayfinding.ts `minimapProjector`):
  // up the map is up the screen, so the map and the view always agree.
  const centre = self ?? pts[0] ?? { x: 0, z: 0 };
  const map = minimapProjector(pts, centre, cameraYaw, 168);
  const [ox, oy] = map(centre.x, centre.z);
  const [ux, uy] = map(centre.x + 1, centre.z);
  const pxPerM = Math.hypot(ux - ox, uy - oy);
  const pin = waypoint ? map(waypoint.x, waypoint.z) : null;
  return (
    <svg width={168} height={168} data-testid="hud-minimap-svg">
      {/* the tracked objective: an exact ring on a fixed place, a shaded circle on an area */}
      {pin && waypoint?.kind === 'area' ? (
        <circle
          cx={pin[0]}
          cy={pin[1]}
          r={Math.max(6, (waypoint.radiusM ?? 0) * pxPerM)}
          fill="rgba(243, 201, 105, 0.18)"
          stroke="#f3c969"
          strokeWidth={1.2}
          strokeDasharray="4 3"
          data-testid="minimap-objective-area"
        >
          <title>{`${waypoint.label} — search this area`}</title>
        </circle>
      ) : pin ? (
        <circle
          cx={pin[0]}
          cy={pin[1]}
          r={6}
          fill="none"
          stroke="#f3c969"
          strokeWidth={2}
          data-testid="minimap-objective-pin"
        >
          <title>{waypoint?.label}</title>
        </circle>
      ) : null}
      {/* resource nodes near the player — green when available, dim when depleted */}
      {nodes.map((n) => {
        const [cx, cy] = map(n.x, n.z);
        return (
          <circle
            key={`n${n.id}`}
            cx={cx}
            cy={cy}
            r={n.resource === findResource ? 4 : 2.5}
            fill={n.depleted ? '#3a4a6e' : n.resource === findResource ? '#ffd75e' : '#6fcf97'}
            data-testid={n.resource === findResource ? 'minimap-target-node' : undefined}
          >
            <title>
              {nameOf('resources', n.resource)}
              {n.depleted ? ' (depleted)' : ''}
            </title>
          </circle>
        );
      })}
      {/* NPCs — gold diamonds; a "!" over each one a quest is ready to hand in to (AD-867) */}
      {npcs.map((npc) => {
        const [cx, cy] = map(npc.x, npc.z);
        const ready = turnInAt.has(npc.id);
        return (
          <g key={`npc${npc.id}`}>
            <rect
              x={cx - 3.5}
              y={cy - 3.5}
              width={7}
              height={7}
              transform={`rotate(45 ${cx} ${cy})`}
              fill="#f3c969"
              stroke="#1a1208"
              strokeWidth={0.7}
            >
              <title>
                {[npc.name, npcRolesText(npc.roles), ready ? 'ready to turn in' : '']
                  .filter((t) => t !== '')
                  .join(' · ')}
              </title>
            </rect>
            {ready ? (
              <text
                x={cx}
                y={cy - 6}
                textAnchor="middle"
                fontSize={13}
                fontWeight={700}
                fill="#ffd75e"
                stroke="#1a1208"
                strokeWidth={0.8}
                data-testid="minimap-turn-in"
              >
                {NPC_MARKER_GLYPHS.turnIn}
              </text>
            ) : null}
          </g>
        );
      })}
      {/* entities — self cyan, enemies red (near only), others slate */}
      {entities.map((e) => {
        if (e.x == null || e.z == null) return null;
        const [cx, cy] = map(e.x, e.z);
        const isSelf = e.id === state.selfEntityId;
        return (
          <circle
            key={`e${e.id}`}
            cx={cx}
            cy={cy}
            r={isSelf ? 4 : 3}
            fill={isSelf ? '#3fc7d4' : e.kind === 'enemy' ? '#ff6b6b' : '#9fb4cf'}
          />
        );
      })}
    </svg>
  );
}

/**
 * The gather channel's progress (AD-665): the resource and a bar filling over the channel's
 * length, redrawn each animation frame from the press time. Presentation only — the server's
 * clock decides the yield; the bar just shows the same shared number counting down.
 */
function GatherChannelBar({
  channel,
}: {
  channel: { resource: string; startedAtMs: number; durationMs: number };
}): JSX.Element {
  const [now, setNow] = useState(() => performance.now());
  useEffect(() => {
    let raf = 0;
    const tick = (): void => {
      setNow(performance.now());
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  const frac = Math.max(0, Math.min(1, (now - channel.startedAtMs) / channel.durationMs));
  return (
    <div className="hud-gather-channel" data-testid="gather-channel">
      <div style={{ fontSize: 12, marginBottom: 4 }}>
        Gathering {nameOf('resources', channel.resource)}…
      </div>
      <div
        style={{
          width: 220,
          height: 8,
          background: 'rgba(0,0,0,0.45)',
          border: '1px solid rgba(255,255,255,0.25)',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${Math.round(frac * 100)}%`,
            height: '100%',
            background: 'var(--pxl-shard, #e6b450)',
          }}
        />
      </div>
    </div>
  );
}
